import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Logo, LogoWhite } from '../components/Logo';
import { Button } from '../components/ui/button';
import {
  ChevronRight, ShieldCheck, BarChart3, FileSignature, Zap,
  QrCode, DoorOpen, Users, Building, Brain, Printer, CreditCard,
  AlertTriangle, Lock, Eye, Smartphone, Wifi, ArrowRight, Check, X
} from 'lucide-react';

const IMG = {
  hero: "https://static.prod-images.emergentagent.com/jobs/352d0214-d092-4f94-9abe-748f0c6ca38f/images/47447d7416947771be8f28088b98fe103a679ff08bc9a43ba822d51c9eb41ff6.png",
  chaos: "https://static.prod-images.emergentagent.com/jobs/352d0214-d092-4f94-9abe-748f0c6ca38f/images/3b46e25f72b5656664eda0622ee2e5b18ef29627df9643e793c9e9686f4c69d8.png",
  expo: "https://static.prod-images.emergentagent.com/jobs/352d0214-d092-4f94-9abe-748f0c6ca38f/images/ef96e414230d36cbb10fb9ee6024917cc114c31444e1e87af2351c4f80bcddf3.png",
  legal: "https://static.prod-images.emergentagent.com/jobs/352d0214-d092-4f94-9abe-748f0c6ca38f/images/90c650c1b7740fad2655e9af233014fec909e52bd8217dad2e01591eb4ad3c91.png",
  landing: "https://static.prod-images.emergentagent.com/jobs/4e66200b-bf67-433e-89ff-a14bb7a92c4a/images/07168106b57157ea7d1bbbf031a2895bf2127601ad11a1c0f81fb31e51c0cba3.png",
  badges: "https://static.prod-images.emergentagent.com/jobs/4e66200b-bf67-433e-89ff-a14bb7a92c4a/images/0df35ab36514d652ba913348de714a87115e69c9a813244b942b02433fdefb5f.png",
  access: "https://static.prod-images.emergentagent.com/jobs/4e66200b-bf67-433e-89ff-a14bb7a92c4a/images/e502bb17c8d71f9a35e17c6ac55204c09410aa055f40d9f73d9288d9b73e2a70.png",
  payments: "https://static.prod-images.emergentagent.com/jobs/4e66200b-bf67-433e-89ff-a14bb7a92c4a/images/8582a2caa13f78a8a60d2423004cd1b4a7ed24a7afe2d149e9897813707a794a.png",
  cta: "https://static.prod-images.emergentagent.com/jobs/4e66200b-bf67-433e-89ff-a14bb7a92c4a/images/7cf7ad0346bf8d18cb65d25eedac6cf400618c18e82d587e4a4451b666f52590.png",
};

function SectionTag({ children }) {
  return (
    <div className="inline-flex items-center gap-2 px-3 py-1 border border-blue-500/30 rounded-full text-blue-400 text-xs font-medium tracking-wider uppercase mb-6">
      <span className="w-1 h-1 bg-blue-400 rounded-full" />
      {children}
    </div>
  );
}

export function HomePage() {
  const [activeTier, setActiveTier] = useState(1);

  const tiers = [
    {
      name: 'EXPRESS',
      tagline: 'Eventos corporativos privados',
      desc: 'Importacion de listados, acreditaciones instantaneas y confirmacion de asistencia. Sin complicaciones.',
      features: ['Gestion de asistentes', 'Importacion Excel/CSV', 'Impresion de badges', 'Reportes basicos', 'Presupuesto y pagos'],
      noFeatures: ['Web publica', 'Control de accesos', 'Salas y sesiones', 'Expositores'],
      color: 'from-slate-600 to-slate-700',
      accent: 'text-slate-300',
      border: 'border-slate-600',
    },
    {
      name: 'CONTROLADO',
      tagline: 'Saber quien esta dentro',
      desc: 'Registro online, QR por email y control real de entrada/salida. Si ya esta dentro, el sistema lo detecta.',
      features: ['Todo de Express', 'Web publica + registro', 'Email con QR', 'Control de puertas', 'Sponsors', 'Asistente de IA', 'Logs de email'],
      noFeatures: ['Salas y sesiones', 'Expositores'],
      color: 'from-blue-700 to-blue-800',
      accent: 'text-blue-300',
      border: 'border-blue-600',
    },
    {
      name: 'CON SALAS',
      tagline: 'Aforo por tipo de pase',
      desc: 'Si no han pagado la sala VIP, el sistema los bloquea. Sin errores humanos. Control por sala y sesion.',
      features: ['Todo de Controlado', 'Gestion de salas', 'Sesiones y ponentes', 'Control de acceso por sala', 'Agenda publica'],
      noFeatures: ['Expositores y leads'],
      color: 'from-cyan-700 to-teal-800',
      accent: 'text-cyan-300',
      border: 'border-cyan-600',
    },
    {
      name: 'PREMIUM',
      tagline: 'El evento como negocio',
      desc: 'Convierte tu evento en un motor comercial. Stands, leads, RGPD automatico, app de expositor y monetizacion total.',
      features: ['Todo de Con Salas', 'Gestion de expositores', 'Tipos de stand + cuotas', 'App movil de leads (PWA)', 'Clausulas RGPD auto', 'Exportacion a Excel/CRM'],
      noFeatures: [],
      color: 'from-amber-600 to-orange-700',
      accent: 'text-amber-300',
      border: 'border-amber-500',
    },
  ];

  return (
    <div className="min-h-screen bg-slate-950 text-white" data-testid="landing-page">

      {/* ═══ Header ═══ */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-slate-950/80 backdrop-blur-lg border-b border-white/5">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
          <LogoWhite size="md" />
          <div className="flex items-center gap-3">
            <Button variant="ghost" className="text-slate-400 hover:text-white text-sm" asChild>
              <Link to="/admin/login">Iniciar sesion</Link>
            </Button>
            <Button className="bg-blue-600 hover:bg-blue-500 text-sm font-semibold h-9 px-5" asChild>
              <Link to="/admin/register">Solicitar Demo</Link>
            </Button>
          </div>
        </div>
      </header>

      {/* ═══ 1. HERO ═══ */}
      <section className="relative min-h-screen flex items-center overflow-hidden pt-16">
        <div className="absolute inset-0">
          <img src={IMG.hero} alt="Control de acceso" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-slate-950 via-slate-950/85 to-slate-950/40" />
        </div>
        <div className="relative z-10 max-w-6xl mx-auto px-4 w-full">
          <div className="max-w-2xl">
            <SectionTag>Plataforma de control total</SectionTag>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 leading-[1.08] tracking-tight" data-testid="hero-title">
              No vendemos QR.
              <br />
              <span className="bg-gradient-to-r from-blue-400 to-cyan-300 bg-clip-text text-transparent">
                Vendemos proteccion
              </span>
              <br />
              de ingresos.
            </h1>
            <p className="text-base sm:text-lg text-slate-300 mb-10 leading-relaxed max-w-lg">
              ENTRICODE es el sistema de control operativo y economico que garantiza que nadie entre sin permiso, que cada sala VIP sea respetada y que cada euro este bajo control.
            </p>
            <div className="flex flex-wrap gap-4">
              <Button size="lg" className="bg-blue-600 hover:bg-blue-500 h-12 px-8 text-base font-semibold shadow-lg shadow-blue-600/25" asChild>
                <Link to="/admin/register" data-testid="get-started-btn">
                  Solicitar Demo de Control Real <ChevronRight className="w-4 h-4 ml-1" />
                </Link>
              </Button>
            </div>
            <div className="flex items-center gap-8 mt-14 pt-8 border-t border-white/10">
              {[
                { n: 'Tiempo real', d: 'Datos exactos' },
                { n: 'RGPD', d: 'Cumplimiento legal' },
                { n: 'Firma e-', d: 'Ley 6/2020 + eIDAS' },
                { n: 'PWA', d: 'Sin instalacion' },
              ].map((b, i) => (
                <div key={i} className="text-center">
                  <div className="text-sm font-bold text-white">{b.n}</div>
                  <div className="text-[11px] text-slate-500">{b.d}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ═══ 2. EL PROBLEMA ═══ */}
      <section className="relative py-24 overflow-hidden">
        <div className="absolute inset-0">
          <img src={IMG.chaos} alt="Caos en eventos" className="w-full h-full object-cover opacity-30" />
          <div className="absolute inset-0 bg-gradient-to-b from-slate-950 via-red-950/20 to-slate-950" />
        </div>
        <div className="relative z-10 max-w-6xl mx-auto px-4">
          <div className="text-center mb-14">
            <SectionTag>El problema</SectionTag>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-4">
              Que esta pasando <span className="text-red-400">realmente</span> en tus eventos?
            </h2>
            <p className="text-slate-400 max-w-2xl mx-auto">
              Muchos sistemas solo hacen "pip", pero no evitan los problemas reales.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
            {[
              { icon: <QrCode className="w-6 h-6" />, title: 'QR duplicados', desc: 'Un asistente comparte su QR y entran 3 personas con la misma acreditacion. Dinero perdido.', color: 'text-red-400 bg-red-500/10 border-red-500/20' },
              { icon: <DoorOpen className="w-6 h-6" />, title: 'Acceso VIP sin control', desc: 'Personal que "deja pasar" sin verificacion. Patrocinadores que pagan por exclusividad y no la obtienen.', color: 'text-orange-400 bg-orange-500/10 border-orange-500/20' },
              { icon: <Eye className="w-6 h-6" />, title: 'Sin trazabilidad', desc: 'No sabes cuantas personas hay dentro realmente. Datos de permanencia inventados. Picos invisibles.', color: 'text-amber-400 bg-amber-500/10 border-amber-500/20' },
              { icon: <BarChart3 className="w-6 h-6" />, title: 'Metricas falsas', desc: 'Los patrocinadores piden datos y solo puedes dar estimaciones. Los expositores no saben si fue rentable.', color: 'text-yellow-400 bg-yellow-500/10 border-yellow-500/20' },
            ].map((p, i) => (
              <div key={i} className={`p-6 rounded-xl border ${p.color} backdrop-blur-sm`}>
                <div className="mb-4">{p.icon}</div>
                <h3 className="font-bold text-white mb-2">{p.title}</h3>
                <p className="text-sm text-slate-300 leading-relaxed">{p.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ 3. LA SOLUCION ═══ */}
      <section className="py-24 bg-slate-900/50">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-16">
            <SectionTag>La solucion</SectionTag>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-4">
              Control operativo y economico <span className="text-blue-400">real</span>
            </h2>
            <p className="text-slate-400 max-w-2xl mx-auto">
              No es solo un sistema de acreditacion. Es una gestion integral disenada para garantizar control total.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: <ShieldCheck className="w-7 h-7" />, title: 'Control de accesos real', desc: 'Validacion de unicidad. Si ya esta dentro, el sistema lo detecta automaticamente.', color: 'text-blue-400' },
              { icon: <CreditCard className="w-7 h-7" />, title: 'Monetizacion eficiente', desc: 'Cobra entradas, VIP, stands. Cada euro bajo control con Stripe integrado.', color: 'text-emerald-400' },
              { icon: <FileSignature className="w-7 h-7" />, title: 'Cumplimiento legal', desc: 'RGPD, firma electronica, contratos versionados. Todo conforme a la ley espanola y europea.', color: 'text-amber-400' },
              { icon: <BarChart3 className="w-7 h-7" />, title: 'Datos en tiempo real', desc: 'Personas dentro ahora mismo. Picos horarios. Aforo por sala. No estimaciones.', color: 'text-cyan-400' },
            ].map((s, i) => (
              <div key={i} className="p-6 rounded-xl bg-slate-800/50 border border-slate-700/50 hover:border-slate-600 transition-colors group">
                <div className={`${s.color} mb-4 group-hover:scale-110 transition-transform`}>{s.icon}</div>
                <h3 className="font-bold text-white mb-2">{s.title}</h3>
                <p className="text-sm text-slate-400 leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ 4. LOS 4 NIVELES ═══ */}
      <section className="py-24">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-12">
            <SectionTag>Niveles de control</SectionTag>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-4">
              Adaptamos la tecnologia a <span className="text-blue-400">tu evento</span>
            </h2>
            <p className="text-slate-400 max-w-2xl mx-auto">
              4 niveles progresivos. Desde un evento privado de 50 personas hasta una feria con 10.000 asistentes y 200 expositores.
            </p>
          </div>

          {/* Tier selector */}
          <div className="flex flex-wrap justify-center gap-2 mb-10">
            {tiers.map((t, i) => (
              <button key={i} onClick={() => setActiveTier(i)}
                className={`px-5 py-2.5 rounded-lg text-sm font-semibold transition-all ${activeTier === i ? `bg-gradient-to-r ${t.color} text-white shadow-lg` : 'bg-slate-800/80 text-slate-400 hover:text-white border border-slate-700'}`}
                data-testid={`tier-btn-${i}`}>
                {t.name}
              </button>
            ))}
          </div>

          {/* Active tier detail */}
          <div className={`rounded-2xl border p-8 sm:p-10 ${tiers[activeTier].border} bg-slate-900/80`} data-testid="tier-detail">
            <div className="grid md:grid-cols-2 gap-8">
              <div>
                <div className={`text-xs font-bold tracking-widest uppercase mb-2 ${tiers[activeTier].accent}`}>
                  {tiers[activeTier].name}
                </div>
                <h3 className="text-2xl font-bold text-white mb-3">{tiers[activeTier].tagline}</h3>
                <p className="text-slate-300 mb-8 leading-relaxed">{tiers[activeTier].desc}</p>
                <Button className="bg-blue-600 hover:bg-blue-500 font-semibold gap-2" asChild>
                  <Link to="/admin/register">
                    Empezar con {tiers[activeTier].name} <ArrowRight className="w-4 h-4" />
                  </Link>
                </Button>
              </div>
              <div>
                <div className="space-y-2.5">
                  {tiers[activeTier].features.map((f, i) => (
                    <div key={i} className="flex items-center gap-3 text-sm">
                      <Check className="w-4 h-4 text-emerald-400 shrink-0" />
                      <span className="text-slate-200">{f}</span>
                    </div>
                  ))}
                  {tiers[activeTier].noFeatures.map((f, i) => (
                    <div key={`n-${i}`} className="flex items-center gap-3 text-sm">
                      <X className="w-4 h-4 text-slate-600 shrink-0" />
                      <span className="text-slate-500">{f}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ═══ 5. CARACTERISTICAS KILLER ═══ */}
      <section className="py-24 bg-slate-900/50">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-16">
            <SectionTag>Funcionalidades clave</SectionTag>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-4">
              Lo que nos hace <span className="text-blue-400">diferentes</span>
            </h2>
          </div>

          {/* Feature 1: Hardware */}
          <div className="grid md:grid-cols-2 gap-8 items-center mb-20">
            <div className="rounded-xl overflow-hidden h-72">
              <img src={IMG.badges} alt="Impresion de badges" className="w-full h-full object-cover" />
            </div>
            <div>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center">
                  <Printer className="w-5 h-5 text-blue-400" />
                </div>
                <h3 className="text-xl font-bold">Hardware Sincronizado</h3>
              </div>
              <p className="text-slate-300 leading-relaxed mb-4">
                Tablets desasistidas, impresoras Zebra instantaneas y control por movil. Todo conectado en tiempo real via WebSocket seguro.
              </p>
              <div className="space-y-2 text-sm text-slate-400">
                <div className="flex items-center gap-2"><Wifi className="w-4 h-4 text-blue-400" /> Print Relay: imprime desde iPad sin cables</div>
                <div className="flex items-center gap-2"><Smartphone className="w-4 h-4 text-blue-400" /> Funciona como PWA en cualquier dispositivo</div>
                <div className="flex items-center gap-2"><Zap className="w-4 h-4 text-blue-400" /> Reconexion automatica con cola de trabajos</div>
              </div>
            </div>
          </div>

          {/* Feature 2: Monetizacion */}
          <div className="grid md:grid-cols-2 gap-8 items-center mb-20">
            <div className="order-2 md:order-1">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-lg bg-emerald-500/10 flex items-center justify-center">
                  <CreditCard className="w-5 h-5 text-emerald-400" />
                </div>
                <h3 className="text-xl font-bold">Monetizacion Directa</h3>
              </div>
              <p className="text-slate-300 leading-relaxed mb-4">
                Cobra entradas por tipo de pase, vende acceso a salas VIP, gestiona stands de expositores. Todo integrado con Stripe. El organizador cobra directamente a sus asistentes.
              </p>
              <div className="space-y-2 text-sm text-slate-400">
                <div className="flex items-center gap-2"><Check className="w-4 h-4 text-emerald-400" /> Pases Basic, VIP y Premium con precios distintos</div>
                <div className="flex items-center gap-2"><Check className="w-4 h-4 text-emerald-400" /> El organizador conecta su propio Stripe</div>
                <div className="flex items-center gap-2"><Check className="w-4 h-4 text-emerald-400" /> Cobro automatico al registro + confirmacion</div>
              </div>
            </div>
            <div className="rounded-xl overflow-hidden h-72 order-1 md:order-2">
              <img src={IMG.payments} alt="Pagos integrados" className="w-full h-full object-cover" />
            </div>
          </div>

          {/* Feature 3: IA */}
          <div className="grid md:grid-cols-2 gap-8 items-center mb-20">
            <div className="rounded-xl overflow-hidden h-72">
              <img src={IMG.landing} alt="Landing page" className="w-full h-full object-cover" />
            </div>
            <div>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-lg bg-purple-500/10 flex items-center justify-center">
                  <Brain className="w-5 h-5 text-purple-400" />
                </div>
                <h3 className="text-xl font-bold">Asistente IA 24/7</h3>
              </div>
              <p className="text-slate-300 leading-relaxed mb-4">
                Un agente inteligente que responde dudas de los asistentes basandose en documentos reales del evento: programa, FAQ, normativa, instrucciones. Reduce carga administrativa.
              </p>
              <div className="space-y-2 text-sm text-slate-400">
                <div className="flex items-center gap-2"><Brain className="w-4 h-4 text-purple-400" /> GPT con contexto documental del evento</div>
                <div className="flex items-center gap-2"><Users className="w-4 h-4 text-purple-400" /> Avatar y personalidad configurables</div>
                <div className="flex items-center gap-2"><Zap className="w-4 h-4 text-purple-400" /> Respuestas precisas, no genericas</div>
              </div>
            </div>
          </div>

          {/* Feature 4: Legal */}
          <div className="grid md:grid-cols-2 gap-8 items-center">
            <div className="order-2 md:order-1">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-lg bg-amber-500/10 flex items-center justify-center">
                  <FileSignature className="w-5 h-5 text-amber-400" />
                </div>
                <h3 className="text-xl font-bold">Gestion Contractual Legal</h3>
              </div>
              <p className="text-slate-300 leading-relaxed mb-4">
                Generacion automatica de presupuestos, firma electronica legalmente vinculante y PDF unico con contrato, anexo RGPD y SLA. Registro de auditoria con hash SHA-256, IP y timestamp.
              </p>
              <div className="space-y-2 text-sm text-slate-400">
                <div className="flex items-center gap-2"><Lock className="w-4 h-4 text-amber-400" /> Ley 6/2020 + Reglamento eIDAS</div>
                <div className="flex items-center gap-2"><FileSignature className="w-4 h-4 text-amber-400" /> Documentos versionados e inmutables</div>
                <div className="flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-amber-400" /> Clausulas RGPD automaticas para leads</div>
              </div>
            </div>
            <div className="rounded-xl overflow-hidden h-72 order-1 md:order-2">
              <img src={IMG.legal} alt="Firma digital" className="w-full h-full object-cover" />
            </div>
          </div>
        </div>
      </section>

      {/* ═══ 6. DIFERENCIACION ═══ */}
      <section className="py-24">
        <div className="max-w-5xl mx-auto px-4">
          <div className="text-center mb-14">
            <SectionTag>Comparativa</SectionTag>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-4">
              Por que <span className="text-blue-400">ENTRICODE</span>
            </h2>
          </div>

          <div className="rounded-2xl border border-slate-800 overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-800/80">
                  <th className="text-left py-4 px-6 text-slate-400 font-medium">Lo que otros hacen</th>
                  <th className="text-left py-4 px-6 text-blue-400 font-medium">Lo que hace ENTRICODE</th>
                </tr>
              </thead>
              <tbody>
                {[
                  ['Check-in basico', 'Control de flujo entrada/salida en tiempo real'],
                  ['Imprimen badges', 'Niveles de acceso inteligentes por tipo de pase'],
                  ['Cuentan asistentes', 'Saben cuantos estan DENTRO ahora mismo'],
                  ['Cobran entradas', 'Monetizacion total: entradas + VIP + expositores + stands'],
                  ['Generan facturas', 'Contratos legales con firma electronica y auditoria completa'],
                  ['Ignoran la ley', 'Cumplen RGPD con consentimientos granulares y clausulas auto'],
                ].map(([other, us], i) => (
                  <tr key={i} className="border-t border-slate-800/50 hover:bg-slate-800/20 transition-colors">
                    <td className="py-3.5 px-6 text-slate-500 flex items-center gap-2">
                      <X className="w-3.5 h-3.5 text-red-400/60 shrink-0" /> {other}
                    </td>
                    <td className="py-3.5 px-6 text-slate-200 flex items-center gap-2">
                      <Check className="w-3.5 h-3.5 text-emerald-400 shrink-0" /> {us}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-center text-slate-500 text-sm mt-6">
            Si se vende una entrada, entra una sola persona. En eventos grandes, esto <span className="text-white font-medium">ahorra miles de euros</span>.
          </p>
        </div>
      </section>

      {/* ═══ 7. DATOS REALES ═══ */}
      <section className="py-24 bg-slate-900/50">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-14">
            <SectionTag>Metricas en tiempo real</SectionTag>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-4">
              Datos reales, no <span className="text-red-400 line-through">estimaciones</span>
            </h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {[
              { n: '847', l: 'Personas dentro', sub: 'Ahora mismo', color: 'text-emerald-400' },
              { n: '1.234', l: 'Acreditados', sub: 'Del total', color: 'text-blue-400' },
              { n: '14:30', l: 'Pico horario', sub: 'Mayor afluencia', color: 'text-cyan-400' },
              { n: '92%', l: 'VIP en sala A', sub: 'Aforo actual', color: 'text-amber-400' },
              { n: '456', l: 'Leads capturados', sub: 'Por expositores', color: 'text-purple-400' },
              { n: '97.2%', l: 'QR validos', sub: 'Sin duplicados', color: 'text-emerald-400' },
            ].map((m, i) => (
              <div key={i} className="p-5 rounded-xl bg-slate-800/50 border border-slate-700/50 text-center">
                <div className={`text-2xl font-bold ${m.color} mb-1`}>{m.n}</div>
                <div className="text-xs font-medium text-white mb-0.5">{m.l}</div>
                <div className="text-[10px] text-slate-500">{m.sub}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ 8. EXPOSITORES ═══ */}
      <section className="py-24">
        <div className="max-w-6xl mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-10 items-center">
            <div className="rounded-xl overflow-hidden h-96">
              <img src={IMG.expo} alt="Expositor capturando leads" className="w-full h-full object-cover" />
            </div>
            <div>
              <SectionTag>Para expositores</SectionTag>
              <h2 className="text-2xl sm:text-3xl font-bold mb-4">
                App de captura de leads con <span className="text-blue-400">RGPD integrado</span>
              </h2>
              <p className="text-slate-300 mb-6 leading-relaxed">
                Cada expositor recibe su propia app movil. Escanea el badge del asistente, captura el lead y lo exporta a Excel o CRM. Con consentimiento legal garantizado.
              </p>
              <div className="space-y-3 text-sm">
                {[
                  'Escaner QR desde camara del movil',
                  'Notas por lead para anotar conversaciones',
                  'Exportacion a Excel con un clic',
                  'Cuota de pases por tipo de stand',
                  'Consentimiento RGPD individual por expositor',
                  'Datos limitados si el asistente no dio consentimiento',
                ].map((f, i) => (
                  <div key={i} className="flex items-center gap-3 text-slate-300">
                    <Check className="w-4 h-4 text-emerald-400 shrink-0" /> {f}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ═══ 9. CUMPLIMIENTO LEGAL ═══ */}
      <section className="py-24 bg-slate-900/50">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-14">
            <SectionTag>Cumplimiento legal</SectionTag>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-4">
              Tu evento cumple con la ley <span className="text-blue-400">desde el primer clic</span>
            </h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {[
              { icon: <FileSignature className="w-6 h-6" />, title: 'Firma electronica legal', desc: 'Conforme a Ley 6/2020, LSSI-CE y Reglamento eIDAS. Hash SHA-256, IP y timestamp en cada firma.', color: 'text-blue-400 bg-blue-500/10 border-blue-500/20' },
              { icon: <ShieldCheck className="w-6 h-6" />, title: 'RGPD integrado', desc: 'Consentimiento individual, clausulas automaticas, anexo de encargo de tratamiento versionado.', color: 'text-emerald-400 bg-emerald-500/10 border-emerald-500/20' },
              { icon: <Lock className="w-6 h-6" />, title: 'Contratos versionados', desc: 'Contrato, RGPD y SLA inmutables. Cada firma registra la version exacta del documento.', color: 'text-amber-400 bg-amber-500/10 border-amber-500/20' },
              { icon: <Eye className="w-6 h-6" />, title: 'Auditoria completa', desc: 'Codigo de contrato unico, datos del firmante, IP, timestamp UTC y hash de integridad.', color: 'text-cyan-400 bg-cyan-500/10 border-cyan-500/20' },
              { icon: <AlertTriangle className="w-6 h-6" />, title: 'Bloqueo post-firma', desc: 'Tras firmar, nada se modifica. Si cambia el presupuesto, se requiere nueva firma obligatoria.', color: 'text-red-400 bg-red-500/10 border-red-500/20' },
              { icon: <Building className="w-6 h-6" />, title: 'Leads con responsabilidad', desc: 'El expositor acepta ser responsable independiente del tratamiento antes de capturar datos.', color: 'text-purple-400 bg-purple-500/10 border-purple-500/20' },
            ].map((c, i) => (
              <div key={i} className={`p-6 rounded-xl border ${c.color}`}>
                <div className="mb-4">{c.icon}</div>
                <h3 className="font-bold text-white mb-2">{c.title}</h3>
                <p className="text-sm text-slate-300 leading-relaxed">{c.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ 10. CTA FINAL ═══ */}
      <section className="py-32 relative overflow-hidden">
        <div className="absolute inset-0">
          <img src={IMG.cta} alt="Evento profesional" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-slate-950/80 backdrop-blur-[2px]" />
        </div>
        <div className="max-w-4xl mx-auto px-4 text-center relative z-10">
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-4">
            No vendemos un software.
          </h2>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-blue-400 mb-8">
            Vendemos tranquilidad operativa.
          </h2>
          <p className="text-lg text-slate-300 mb-6 max-w-xl mx-auto">
            Ideal para organizadores profesionales, ferias sectoriales, congresos medicos y agencias que revenden servicios de gestion.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Button size="lg" className="bg-blue-600 hover:bg-blue-500 h-13 px-8 text-base font-semibold shadow-lg shadow-blue-600/25" asChild>
              <Link to="/admin/register">
                Solicitar Demo de Control Real <ChevronRight className="w-4 h-4 ml-1" />
              </Link>
            </Button>
            <Button size="lg" variant="outline" className="h-13 px-8 text-base border-white/20 text-white hover:bg-white/10 hover:text-white" asChild>
              <Link to="/admin/login">Ya tengo cuenta</Link>
            </Button>
          </div>
        </div>
      </section>

      {/* ═══ Footer ═══ */}
      <footer className="py-14 bg-slate-950 border-t border-slate-800">
        <div className="max-w-6xl mx-auto px-4">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-6">
            <div>
              <LogoWhite size="md" />
              <p className="text-slate-500 text-sm mt-2">Plataforma de Control Total para Eventos</p>
            </div>
            <div className="text-center sm:text-right">
              <p className="text-slate-600 text-xs">
                &copy; 2026 ENTRICODE. Todos los derechos reservados.
              </p>
              <p className="text-slate-700 text-xs mt-1">
                Firma electronica conforme a Ley 6/2020, LSSI-CE y Reglamento eIDAS
              </p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default HomePage;
